/**
 * Utilidades para manejar IDs de eventos, especialmente eventos recurrentes
 */

/**
 * Extrae el ID base de un evento, eliminando el sufijo de fecha si existe.
 * Los eventos recurrentes pueden tener IDs con formato: {baseId}_{timestamp}
 * Ejemplo: "69a722567f4f3905181c5bfa_20260314180200" -> "69a722567f4f3905181c5bfa"
 * 
 * @param eventId - El ID del evento que puede contener un sufijo de timestamp
 * @returns El ID base sin el sufijo de timestamp
 */
export const extractBaseEventId = (eventId: string): string => {
  if (!eventId) return eventId;
  
  // Si el ID contiene un guión bajo, podría ser una ocurrencia de evento recurrente
  const underscoreIndex = eventId.indexOf('_');
  
  if (underscoreIndex === -1) {
    // No hay guión bajo, retornar el ID tal cual
    return eventId;
  }
  
  // Extraer la parte antes del guión bajo
  const baseId = eventId.substring(0, underscoreIndex);
  const suffix = eventId.substring(underscoreIndex + 1);
  
  // Verificar si el sufijo parece ser un timestamp (14 dígitos: YYYYMMDDHHmmss)
  // Si es así, retornar solo el baseId
  if (/^\d{14}$/.test(suffix)) {
    return baseId;
  }
  
  // Si el sufijo no es un timestamp, retornar el ID completo
  return eventId;
};

/**
 * Obtiene el ID correcto para usar en operaciones API de eventos.
 * Para eventos recurrentes, usa el parentId si existe.
 * Para eventos normales o cuando no hay parentId, extrae el ID base.
 * 
 * @param event - El evento con posible parentId y originalOccurrenceDate
 * @returns El ID apropiado para usar en llamadas API
 */
export const getEventIdForApi = (event: any): string => {
  if (!event) return '';
  
  // Si es una ocurrencia de evento recurrente (tiene originalOccurrenceDate o parentId),
  // usar el parentId para operaciones que afectan todas las ocurrencias
  if (event.parentId) {
    return event.parentId;
  }
  
  // Si tiene un ID con sufijo pero no tiene parentId, extraer el ID base
  return extractBaseEventId(event.id);
};

/**
 * Verifica si un evento es una ocurrencia de un evento recurrente
 * 
 * @param event - El evento a verificar
 * @returns true si es una ocurrencia recurrente
 */
export const isRecurringOccurrence = (event: any): boolean => {
  return event?.originalOccurrenceDate != null || 
         (event?.id && event.id.includes('_') && /\d{14}$/.test(event.id));
};

/**
 * Obtiene el ID para eliminar una ocurrencia específica de un evento recurrente.
 * Para una ocurrencia específica, usa el ID completo (con sufijo).
 * Para eliminar todas las ocurrencias, usa getEventIdForApi.
 * 
 * @param event - El evento a eliminar
 * @param deleteAll - Si debe eliminar todas las ocurrencias o solo esta
 * @returns El ID apropiado para la operación de eliminación
 */
export const getEventIdForDelete = (event: any, deleteAll: boolean = false): string => {
  if (!event) return '';
  
  // Si queremos eliminar todas las ocurrencias, usar el ID base o parentId
  if (deleteAll) {
    return getEventIdForApi(event);
  }
  
  // Para eliminar solo esta ocurrencia, usar el ID completo si es recurrente
  if (isRecurringOccurrence(event)) {
    return event.id; // ID con sufijo para eliminar solo esta ocurrencia
  }
  
  // Para eventos normales, extraer el ID base
  return extractBaseEventId(event.id);
};
